import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Typography,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import { Pencil, Plus, Trash2, X } from "lucide-react";
import AnimationFadeIn from "@components/AnimationFadeIn";
import Breadcrumb from "@components/Breadcrumb";
import InputCst from "@components/InputCst/InputCst";
import DataPickerCst from "@components/InputCst/DataPickerCst";
import LOOutcomeModalCst from "@components/LOOutcomeModalCst";
import {
  formatSessionOccupiedError,
  getAllErrorsAsString,
  isSessionOverlapError,
} from "@utils/utilsApi";
import { openToast } from "@utils/toastStore";
import { openSessionWizardModal } from "./SessionWizard/host";
import { SessionDraft } from "./SessionWizard/types";
import { sessionToDraft } from "./SessionWizard/editMapping";
import { useEditionDetail } from "./hooks/useEditionDetail";
import { useEditionSessions } from "./hooks/useEditionSessions";
import { useEditionSubmit } from "./hooks/useEditionSubmit";
import { useActivityDuration } from "./hooks/useActivityDuration";
import { EditionKind } from "./types";

interface EditionFormState {
  edition_name: string;
  edition_code: string;
  publish_date: string | null;
  extra_info: string;
}

const INITIAL_FORM: EditionFormState = {
  edition_name: "",
  edition_code: "",
  publish_date: null,
  extra_info: "",
};

const sessionsTotalMinutes = (sessions: SessionDraft[]): number =>
  sessions.reduce((total, s) => {
    if (!s.start_date || !s.end_date) return total;
    return (
      total + Math.abs(dayjs(s.end_date).diff(dayjs(s.start_date), "minute"))
    );
  }, 0);

const AddEditionPage: React.FC = () => {
  const { t } = useTranslation(["editions", "session-wizard"]);
  const theme = useTheme();
  const navigate = useNavigate();
  const { id: corporateId = "", idLO = "", editionId } = useParams();
  const [searchParams] = useSearchParams();

  const kind: EditionKind =
    searchParams.get("kind") === "physical" ? "physical" : "virtual";

  const isEdit = editionId != null && editionId !== "";

  const {
    detail,
    isLoading: detailLoading,
    error: detailError,
  } = useEditionDetail(corporateId, editionId ?? "", kind);
  const { sessions: rawSessions, isLoading: sessionsLoading } =
    useEditionSessions(corporateId, editionId ?? "", kind);

  const loId = isEdit ? detail?.learning_objects?.id : idLO;
  const { activityDuration } = useActivityDuration(
    corporateId,
    isEdit ? undefined : loId,
  );

  const { submitEdition, isSubmitting } = useEditionSubmit(
    corporateId,
    kind,
    editionId,
  );

  const [form, setForm] = useState<EditionFormState>(INITIAL_FORM);
  const [sessions, setSessions] = useState<SessionDraft[]>([]);
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);
  const [hydrated, setHydrated] = useState(!isEdit);
  const [durationWarnOpen, setDurationWarnOpen] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);

  const baselineRef = useRef<{
    form: EditionFormState;
    sessions: SessionDraft[];
  }>({ form: INITIAL_FORM, sessions: [] });

  useEffect(() => {
    if (!isEdit || hydrated) return;
    if (detailLoading || sessionsLoading || !detail) return;
    const hydratedForm: EditionFormState = {
      edition_name: detail.edition_name ?? "",
      edition_code: detail.edition_code ?? "",
      publish_date: detail.publish_date
        ? dayjs(detail.publish_date).toISOString()
        : null,
      extra_info: detail.extra_info ?? "",
    };
    const hydratedSessions = rawSessions.map((s) => sessionToDraft(s, kind));
    setForm(hydratedForm);
    setSessions(hydratedSessions);
    baselineRef.current = { form: hydratedForm, sessions: hydratedSessions };
    setHydrated(true);
  }, [
    isEdit,
    hydrated,
    detailLoading,
    sessionsLoading,
    detail,
    rawSessions,
    kind,
  ]);

  const update = (patch: Partial<EditionFormState>) =>
    setForm((prev) => ({ ...prev, ...patch }));

  const codeTooLong = form.edition_code.length > 12;
  const extraTooLong = form.extra_info.length > 500;
  const publishDateMissing = !form.publish_date;
  const publishDateLocked =
    isEdit &&
    !!detail?.publish_date &&
    dayjs().isAfter(dayjs(detail.publish_date));

  const editionContextLabel =
    detail?.learning_objects?.label || detail?.edition_name || undefined;

  const handleAddSession = () => {
    openSessionWizardModal({
      kind,
      corporateId,
      editionId: isEdit ? editionId : undefined,
      contextLabel: editionContextLabel,
      publishDate: form.publish_date,
      onConfirm: (draft) => setSessions((prev) => [...prev, draft]),
    });
  };

  const handleEditSession = (index: number) => {
    const target = sessions[index];
    if (!target) return;
    openSessionWizardModal({
      kind,
      corporateId,
      editionId: isEdit ? editionId : undefined,
      contextLabel: editionContextLabel,
      editSession: target,
      publishDate: form.publish_date,
      onConfirm: (draft) =>
        setSessions((prev) =>
          prev.map((s, i) => (i === index ? { ...draft, id: s.id } : s)),
        ),
    });
  };

  const handleRemoveSession = (index: number) =>
    setSessions((prev) => prev.filter((_, i) => i !== index));

  const handleReset = () => {
    setForm(isEdit ? baselineRef.current.form : INITIAL_FORM);
    setSessions(isEdit ? baselineRef.current.sessions : []);
    setAttemptedSubmit(false);
  };

  const doSubmit = async () => {
    try {
      await submitEdition(
        {
          lo_id: loId,
          edition_code: form.edition_code || null,
          edition_name: form.edition_name || null,
          extra_info: form.extra_info || null,
          publish_date: form.publish_date,
        },
        sessions,
      );
      setSuccessOpen(true);
    } catch (e: any) {
      openToast({
        severity: "error",
        message: isSessionOverlapError(e)
          ? formatSessionOccupiedError(e, t)
          : getAllErrorsAsString(e) || String(t("feedback.error")),
      });
    }
  };

  const handleConfirm = () => {
    setAttemptedSubmit(true);
    if (publishDateMissing || codeTooLong || extraTooLong) return;
    if (
      !isEdit &&
      activityDuration &&
      sessionsTotalMinutes(sessions) !== activityDuration
    ) {
      setDurationWarnOpen(true);
      return;
    }
    void doSubmit();
  };

  const handleDurationConfirm = () => {
    setDurationWarnOpen(false);
    void doSubmit();
  };

  const handleSuccessDismiss = () => {
    setSuccessOpen(false);
    navigate({ pathname: "..", search: `?kind=${kind}` }, { relative: "path" });
  };

  const sectionTitle = (title: string) => (
    <Typography
      sx={{
        fontSize: "20px",
        fontWeight: 700,
        color: theme.palette.text.primary,
      }}
    >
      {title}
    </Typography>
  );

  if (isEdit && !hydrated) {
    return (
      <AnimationFadeIn>
        <Box sx={{ py: 3 }}>
          <Breadcrumb />
          {detailError ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                py: 8,
                color: theme.palette.text.tertiary,
                fontSize: "14px",
              }}
            >
              {t("feedback.load-error")}
            </Box>
          ) : (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
              <CircularProgress />
            </Box>
          )}
        </Box>
      </AnimationFadeIn>
    );
  }

  return (
    <AnimationFadeIn>
      <Box sx={{ py: 3 }}>
        <Breadcrumb />

        <Typography
          sx={{
            fontSize: "28px",
            fontWeight: 700,
            color: theme.palette.text.primary,
            mb: 3,
          }}
        >
          {isEdit ? t("edit.title") : t("create.title")}
        </Typography>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            "& .MuiOutlinedInput-root": { borderRadius: "8px" },
            "& .MuiOutlinedInput-notchedOutline": { borderRadius: "8px" },
          }}
        >
          {sectionTitle(t("create.main-info"))}

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <InputCst
                id="edition-name"
                label={String(t("create.fields.name"))}
                value={form.edition_name}
                onChange={(e: any) => update({ edition_name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <InputCst
                id="edition-code"
                label={String(t("create.fields.code"))}
                value={form.edition_code}
                onChange={(e: any) => update({ edition_code: e.target.value })}
                error={codeTooLong}
                errorMessage={
                  codeTooLong ? String(t("create.errors.code-too-long")) : ""
                }
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <DataPickerCst
                id="edition-publish-date"
                label={String(t("create.fields.publish-date"))}
                value={form.publish_date}
                onChange={(v: any) =>
                  update({
                    publish_date: v
                      ? typeof v === "string"
                        ? v
                        : v?.toISOString?.()
                      : null,
                  })
                }
                required
                disabled={publishDateLocked}
                error={attemptedSubmit && publishDateMissing}
                errorMessage={
                  attemptedSubmit && publishDateMissing
                    ? String(t("create.errors.publish-date-required"))
                    : ""
                }
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <InputCst
                id="edition-extra-info"
                label={String(t("create.fields.extra-info"))}
                value={form.extra_info}
                onChange={(e: any) => update({ extra_info: e.target.value })}
                error={extraTooLong}
                errorMessage={
                  extraTooLong
                    ? String(t("create.errors.extra-info-too-long"))
                    : ""
                }
              />
            </Grid>
          </Grid>

          {!isEdit && (
            <>
              <Divider sx={{ my: 2 }} />

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                {sectionTitle(t("create.sessions"))}
                <Button
                  variant="text"
                  startIcon={<Plus size={16} />}
                  onClick={handleAddSession}
                  sx={{
                    textTransform: "none",
                    fontWeight: 700,
                    color: theme.palette.primary.main,
                  }}
                >
                  {t("create.add-session")}
                </Button>
              </Box>

              {sessions.length === 0 ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    py: 4,
                    color: theme.palette.text.tertiary,
                    fontSize: "13px",
                    borderRadius: "12px",
                    border: `1px dashed ${theme.palette.divider}`,
                  }}
                >
                  {t("create.sessions-empty")}
                </Box>
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {sessions.map((session, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 2,
                        padding: "12px 16px",
                        borderRadius: "10px",
                        border: `1px solid ${theme.palette.divider}`,
                        backgroundColor: theme.palette.background.paper,
                      }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          sx={{
                            fontSize: "14px",
                            fontWeight: 700,
                            color: theme.palette.text.primary,
                          }}
                        >
                          {session.start_date
                            ? dayjs(session.start_date).format(
                                "DD/MM/YYYY HH:mm",
                              )
                            : "-"}
                          {" — "}
                          {session.end_date
                            ? dayjs(session.end_date).format("DD/MM/YYYY HH:mm")
                            : "-"}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "12px",
                            color: theme.palette.text.tertiary,
                          }}
                        >
                          {"tool" in session
                            ? session.tool
                              ? t(`session-wizard:method.${session.tool}.title`)
                              : "-"
                            : session.site?.label || "-"}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                        }}
                      >
                        <IconButton
                          onClick={() => handleEditSession(index)}
                          aria-label="edit session"
                          sx={{ color: theme.palette.primary.main }}
                        >
                          <Pencil size={18} />
                        </IconButton>
                        <IconButton
                          onClick={() => handleRemoveSession(index)}
                          aria-label="remove session"
                          sx={{ color: theme.palette.primary.main }}
                        >
                          <Trash2 size={18} />
                        </IconButton>
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </>
          )}
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mt: 4,
          }}
        >
          <Button
            variant="outlined"
            onClick={() => navigate(-1)}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              borderRadius: "8px",
              borderColor: theme.palette.divider,
              color: theme.palette.text.primary,
            }}
          >
            {t("back")}
          </Button>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Button
              variant="text"
              onClick={handleReset}
              disabled={isSubmitting}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                color: theme.palette.primary.main,
              }}
            >
              {t("create.reset")}
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirm}
              disabled={isSubmitting}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                borderRadius: "8px",
              }}
            >
              {t("create.confirm")}
            </Button>
          </Box>
        </Box>
      </Box>

      <Dialog
        open={durationWarnOpen}
        onClose={() => setDurationWarnOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            backgroundColor: theme.palette.background.default,
            borderRadius: "16px",
            border: `1px solid ${theme.palette.divider}`,
          },
        }}
      >
        <DialogTitle sx={{ padding: "20px 24px 0" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
            }}
          >
            <Typography
              sx={{
                fontSize: "18px",
                fontWeight: 700,
                color: theme.palette.text.primary,
              }}
            >
              {t("duration-warning.title")}
            </Typography>
            <IconButton
              onClick={() => setDurationWarnOpen(false)}
              aria-label="close"
              sx={{ color: theme.palette.text.primary }}
            >
              <X size={20} />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ padding: "12px 24px" }}>
          <Typography
            sx={{ fontSize: "14px", color: theme.palette.text.secondary }}
          >
            {t("duration-warning.description")}
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{ padding: "12px 24px 20px", justifyContent: "flex-end", gap: 1 }}
        >
          <Button
            variant="outlined"
            onClick={() => setDurationWarnOpen(false)}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              borderRadius: "8px",
              borderColor: theme.palette.divider,
              color: theme.palette.text.primary,
            }}
          >
            {t("duration-warning.cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={handleDurationConfirm}
            sx={{ textTransform: "none", fontWeight: 700, borderRadius: "8px" }}
          >
            {t("duration-warning.confirm")}
          </Button>
        </DialogActions>
      </Dialog>

      <LOOutcomeModalCst
        open={successOpen}
        entityType="edition"
        mode={isEdit ? "edit" : "add"}
        onDismiss={handleSuccessDismiss}
      />
    </AnimationFadeIn>
  );
};

export default AddEditionPage;
